import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { Button, Field, IdChip, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { formatCredits } from '../../lib/format';
import { translate, useT } from '../../lib/i18n';
import { useOpenRecord } from '../../lib/navigation';
import type {
  Paginated,
  ReferralCampaign,
  ReferralCampaignStatus,
  ReferralEventType,
  ReferralRewardStatus,
  ReferralRewardType,
  ReferralStatus,
} from '../../lib/types';

/*
 * Pieces every referral tab shares: how a status is coloured, how an offer is
 * read out, and how a row points at the account behind it.
 */

export type Tone = 'neutral' | 'brand' | 'good' | 'warn' | 'danger';

export const CAMPAIGN_TONES: Record<ReferralCampaignStatus, Tone> = {
  live: 'good',
  scheduled: 'brand',
  ended: 'neutral',
};

export const REFERRAL_TONES: Record<ReferralStatus, Tone> = {
  pending: 'warn',
  qualified: 'good',
  revoked: 'danger',
};

export const REWARD_TONES: Record<ReferralRewardStatus, Tone> = {
  pending: 'brand',
  granted: 'good',
  failed: 'danger',
};

export const EVENT_TONES: Record<ReferralEventType, Tone> = {
  redeemed: 'brand',
  denied: 'warn',
  qualified: 'good',
  revoked: 'danger',
  reward_granted: 'good',
  reward_failed: 'danger',
};

/** `100 cr`, or `7 days of Premium` — a reward as an operator reads it. */
export function formatReward(type: ReferralRewardType, amount: number): string {
  return type === 'free_trial'
    ? translate('referrals.reward.trialDays', { count: amount })
    : formatCredits(amount);
}

/**
 * The whole offer in one sentence — "Bring 3 friends who make their first
 * top-up → 100 cr, once per account". Shown on every card and live in the
 * editor, so what an operator is about to promise is never a guess.
 */
export function describeOffer(
  campaign: Pick<
    ReferralCampaign,
    | 'qualification'
    | 'requiredReferrals'
    | 'rewardType'
    | 'rewardAmount'
    | 'maxRewardsPerReferrer'
  >,
): string {
  const friends = translate(`referrals.offer.${campaign.qualification}`, {
    count: campaign.requiredReferrals,
  });
  const reward = formatReward(campaign.rewardType, campaign.rewardAmount);
  const limit =
    campaign.maxRewardsPerReferrer === null
      ? translate('referrals.offer.unlimited')
      : translate('referrals.offer.limited', {
          count: campaign.maxRewardsPerReferrer,
        });
  return `${friends} → ${reward}, ${limit}`;
}

/** An account id with a way into it. */
export function UserRef({ id, label }: { id: string; label?: string }) {
  const t = useT();
  const openRecord = useOpenRecord();

  return (
    <span className="inline-flex items-center gap-1">
      <IdChip id={id} title={label} />
      <Button
        variant="ghost"
        className="min-h-[28px] px-1.5 py-1"
        aria-label={t('referrals.openAccount')}
        title={t('referrals.openAccount')}
        icon={<ArrowUpRight size={13} />}
        onClick={() => openRecord({ kind: 'user', id })}
      />
    </span>
  );
}

/** ISO instant → the value a `datetime-local` input wants, in local time. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** The reverse: empty means "no date", which the API reads as null. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** The longest device id the backend stores. */
const MAX_DEVICE_ID_LENGTH = 191;

/**
 * Device ids pasted however they come — one per line, comma-separated, out of
 * a spreadsheet column. Split into the ones that could be device ids and the
 * ones that cannot, so the operator is told which line is wrong before
 * anything is sent.
 */
export function parseDeviceIds(text: string): { valid: string[]; invalid: string[] } {
  const parts = text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const fits = (part: string) => part.length <= MAX_DEVICE_ID_LENGTH;
  const valid = [...new Set(parts.filter(fits))];
  const invalid = parts.filter((part) => !fits(part));
  return { valid, invalid };
}

/**
 * Every campaign, for the filter dropdowns. Campaigns number in the tens, so
 * one page of them covers the lot and is cached across tabs.
 */
export function useCampaignOptions() {
  return useQuery({
    queryKey: ['referrals', 'campaign-options'],
    queryFn: () =>
      api.get<Paginated<ReferralCampaign>>('/api/referrals/campaigns', {
        limit: 200,
      }),
    staleTime: 60_000,
    select: (page) => page.items,
  });
}

/** A campaign filter, fed by {@link useCampaignOptions}. */
export function CampaignSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useT();
  const campaigns = useCampaignOptions();

  return (
    <Field label={t('referrals.filter.campaign')}>
      <Select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{t('referrals.filter.anyCampaign')}</option>
        {campaigns.data?.map((campaign) => (
          <option key={campaign.id} value={campaign.id}>
            {campaign.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}
